import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import React, { Fragment } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useSWRConfig } from 'swr'
import { twJoin } from 'tailwind-merge'
import useUser from 'shared/hooks/use-user'
import PinIcon from 'shared/icons/pin-icon'
import { handleWithoutPropagating } from 'shared/utils/handle-without-propagating'
import { usePinPost } from 'modules/community/api/use-pin-post'
import { getPinnedPostsKey } from 'modules/community/api/use-pinned-posts'
import { mutatePostsByPin } from 'modules/community/components/pinned-post/utils'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { PostInterface } from 'modules/community/types/post-interface'
import { PostPinnedToType } from 'modules/community/types/post-pinned-to-type'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'
import { getPostMutationKey, isPinnedIn } from 'modules/community/utils/pinned-post'

interface PostPinnedActionsProps {
  post: PostInterface
}

export const PostPinnedActions = ({ post }: PostPinnedActionsProps) => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const { communityPath, topicPath } = query as { communityPath: string; topicPath?: string }
  const { user } = useUser()
  const { member } = useIsUserMember()
  const { mutate } = useSWRConfig()
  const pinPostMutation = usePinPost({ postId: post.id })

  const handlePinPost = async (post: PostInterface, pinTo: PostPinnedToType) => {
    try {
      await pinPostMutation.trigger({ source: pinTo })

      const shouldRemoveFromGlobal = !!(
        (pinTo === 'community' && !topicPath) ||
        (pinTo === 'topic' && topicPath)
      )

      mutate(
        getPostMutationKey(topicPath ? 'topic' : 'community'),
        mutatePostsByPin({
          post,
          shouldRemove: shouldRemoveFromGlobal,
          modifier: pinnedTo => pinnedTo.concat(pinTo),
        }),
      )

      mutate(
        getPinnedPostsKey(communityPath, topicPath),
        mutatePostsByPin({
          post,
          shouldAppend: shouldRemoveFromGlobal,
          modifier: pinnedTo => pinnedTo.concat(pinTo),
        }),
      )

      toast.success(t('community.actions.pin_post.success'))
    } catch (e) {
      toast.error(t('community.actions.pin_post.error'))
    }
  }

  const [isPostPinnable] = hasPermissions(user, 'posts', ['pin'], {
    post,
    member,
  })

  if (!isPostPinnable) return null

  return (
    <div className="flex items-center">
      <Menu as="div" onClick={handleWithoutPropagating()} className="relative flex size-[30px]">
        <MenuButton onClick={handleWithoutPropagating()} className="size-7">
          <PinIcon className="size-4 text-text-secondary hover:text-brand-primary" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems
            as="ul"
            anchor="bottom end"
            className="flex w-max min-w-[144px] max-w-sm flex-col rounded-[10px] bg-background-secondary px-5 py-3 shadow-[0px_4px_10px_0px_#00000040]"
          >
            {(['community', 'topic'] as PostPinnedToType[]).map(source => {
              const isPinned = isPinnedIn(post, source)
              return (
                <MenuItem key={`pin-target-${source}`} as="li">
                  <button
                    className="flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary focus:text-text-primary/70"
                    onClick={() => handlePinPost(post, source)}
                    disabled={isPinned}
                  >
                    <PinIcon
                      aria-hidden
                      className={twJoin(
                        'size-4 text-text-secondary opacity-0',
                        isPinned && 'opacity-100',
                      )}
                    />
                    <span className="mr-auto">{t(`community.actions.pin_post.${source}`)}</span>
                  </button>
                </MenuItem>
              )
            })}
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}
